import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import * as moment from 'moment';

import { RouteCollector } from '../common/route-collector';
import { TramApiService } from '../tram-api.service';

import { Route } from '../core/route';
import { Stop, LeafletStop } from '../core/stop';
import { StopCollector } from '../common/stop-collector';
import { ScheduleCollector } from '../common/schedule-collector';

@Component({
    selector: 'schedule',
    templateUrl: 'schedule.component.html',
    styleUrls: ['./schedule.component.scss']
})
export class ScheduleComponent implements OnInit, OnDestroy {
    private afterSelectStopHandler: any;
    private afterRouteCollectorInitializedHandler: any;

    private activatedRouteParams: ParamMap;
    private _selectedStop: Stop;
    get selectedStop(): Stop {
        return this._selectedStop;
    }
    set selectedStop(stop: Stop) {
        if (this._selectedStop === stop)
            return;

        this._selectedStop = stop;

        this.stopCollector.select(stop.id);
    }
    routes: InternalRoute[];
    selectedRoute: InternalRoute;
    stopRoutes: InternalRoute[];
    isStopSelected: boolean;
    schedule: ScheduleTime[];
    shortSchedule: ScheduleTime[];
    isStopReverse?: boolean;
    isFullSchedule: boolean;

    constructor(
        private routeCollector: RouteCollector,
        private stopCollector: StopCollector,
        private scheduleCollector: ScheduleCollector,
        private api: TramApiService,
        private activatedRoute: ActivatedRoute) {
        activatedRoute.paramMap.subscribe(p => {
            if (p.keys.length <= 0)
                return;

            this.activatedRouteParams = p;
        });
    }

    ngOnInit() {
        let routeNumber: string;
        let stopId: number;

        if (this.activatedRouteParams) {
            if (this.activatedRouteParams.has('routeNumber')) {
                routeNumber = this.activatedRouteParams.get('routeNumber');
            }

            if (this.activatedRouteParams.has('stopId')) {
                stopId = +this.activatedRouteParams.get('stopId');
            }
        }

        this.afterRouteCollectorInitializedHandler = _ => this.onAfterRouteCollectorInitialized(routeNumber, stopId);
        this.routeCollector.afterInitialize.on(this.afterRouteCollectorInitializedHandler);

        this.afterSelectStopHandler = s => this.onAfterStopSelect(s);
        this.stopCollector.afterSelect.on(this.afterSelectStopHandler);
    }

    ngOnDestroy() {
        this.stopCollector.afterSelect.off(this.afterSelectStopHandler);
        this.routeCollector.afterInitialize.off(this.afterRouteCollectorInitializedHandler);

        this.afterSelectStopHandler = null;
        this.afterRouteCollectorInitializedHandler = null;

        this.routes = [];
        this.selectedRoute = null;
        this.stopRoutes = [];
        this.isStopSelected = false;
        this.schedule = [];
        this.isStopReverse = null;
    }

    select(route: InternalRoute) {
        if (route.isSelected)
            return;

        this.routes.forEach(r => r.isSelected = r.number === route.number && !route.isSelected);
        this.selectedRoute = route;

        if (this.selectedStop) {
            this.highlightCorreсtDirection();
            this.loadStopSchedule();
        }

        this.loadRouteSchedule();
    }

    private onAfterRouteCollectorInitialized(routeNumber: string, stopId: number) {
        this.initRoutes();

        if (!routeNumber) {
            this.selectInitialRoute();
        } else {
            this.selectPredefinedRoute(routeNumber);
        }

        this.selectPredefinedStop(stopId);
    }
    private onAfterStopSelect(stop: Stop) {
        this._selectedStop = stop;

        this.loadStopRoutes();
        this.highlightCorreсtDirection();
        this.loadStopSchedule();
    }

    private initRoutes() {
        this.routes = this.routeCollector.routes.map(r => {
            let route = new InternalRoute(r);
            route.id = r.id;
            route.number = r.number;

            return route;
        });
    }
    private selectInitialRoute() {
        if (this.routes.length > 0) {
            this.select(this.routes[0]);
        }
    }
    private selectPredefinedRoute(routeNumber: string) {
        this.routes.some(r => {
            if (r.number === routeNumber) {
                this.select(r);

                return true;
            }

            return false;
        });
    }
    private selectPredefinedStop(stopId: number) {
        if (stopId === null)
            return;
        this.stopCollector.select(stopId);
    }

    private loadStopRoutes() {
        let stopRoutes = this.routeCollector.getByStop(this.selectedStop.id);
        this.stopRoutes = stopRoutes.map(r => {
            let route = new InternalRoute(r);
            route.id = r.id;
            route.number = r.number;

            return route;
        });
    }
    private highlightCorreсtDirection() {
        if (!this.selectedRoute)
            return;

        this.isStopReverse = null;

        for (let r of this.stopRoutes) {
            if (r.number !== this.selectedRoute.number)
                continue;

            if (r.route.stopIds.indexOf(this.selectedStop.id) >= 0)
                this.isStopReverse = false;
            else if (r.route.reverseStopIds.indexOf(this.selectedStop.id) >= 0)
                this.isStopReverse = true;
            else {
                this.isStopReverse = null;
            }

            break;
        }
    }
    private loadStopSchedule() {
        this.scheduleCollector.getScheduleByStopAndRoute(this.selectedStop.id, this.selectedRoute.id).then(s => {
            if (!s) {
                this.schedule = [];
                this.shortSchedule = [];
                return;
            }
                

            const schedule: ScheduleTime[] = [];
            let now = moment();
            let nowHours = now.hours();
            let indexFirstActiveTime = -1;

            s.departureTimes.forEach((time, index) => {
                let t = moment(time);
                let timeHours = t.hours();
                let scheduleTime = new ScheduleTime();

                //try to find index of first active time
                //to highlight next left times
                if (indexFirstActiveTime < 0) {
                    if (timeHours > nowHours) {
                        indexFirstActiveTime = index;
                    } else if (timeHours === nowHours && t.minutes() > now.minutes()) {
                        indexFirstActiveTime = index;
                    }
                }

                scheduleTime.time = time;
                scheduleTime.isActive = indexFirstActiveTime >= 0 && index >= indexFirstActiveTime;
                schedule.push(scheduleTime);
            });

            this.schedule = schedule;

            let amountRowsBeforeActive = indexFirstActiveTime / 13 | 0;
            let indexStartOfRow = amountRowsBeforeActive * 13;
            if (schedule.length > 26) {
                if (schedule.length - (indexStartOfRow + 1) >= 26) {
                    this.shortSchedule = schedule.slice(indexStartOfRow, indexStartOfRow + 26);
                }
                else {
                    this.shortSchedule = schedule.slice(schedule.length - 26, schedule.length);
                }
            } else {
                this.isFullSchedule = true;
            }
        });
    }
    private loadRouteSchedule() {
        this.api.getRouteCommonSchedules(this.selectedRoute.id, 0, 0).then(schedules => {
            if (!this.selectedRoute)
                return;

            schedules.forEach(s => {
                if (s.isReverseTrip) {
                    this.selectedRoute.reverseScheduleInfo = new RouteScheduleInfo();
                    this.selectedRoute.reverseScheduleInfo.startTime = s.startTime;
                    this.selectedRoute.reverseScheduleInfo.endTime = s.endTime;
                    this.selectedRoute.reverseScheduleInfo.interval = s.interval;

                    let stopIds = this.selectedRoute.route.reverseStopIds;
                    if (stopIds.length > 0) {
                        let startStop = this.stopCollector.get(stopIds[0]);
                        let finishStop = this.stopCollector.get(stopIds[stopIds.length - 1]);

                        if (startStop)
                            this.selectedRoute.reverseScheduleInfo.startStopName = startStop.name;
                        if (finishStop)
                            this.selectedRoute.reverseScheduleInfo.finishStopName = finishStop.name;
                    }
                } else {
                    this.selectedRoute.directScheduleInfo = new RouteScheduleInfo();
                    this.selectedRoute.directScheduleInfo.startTime = s.startTime;
                    this.selectedRoute.directScheduleInfo.endTime = s.endTime;
                    this.selectedRoute.directScheduleInfo.interval = s.interval;

                    let stopIds = this.selectedRoute.route.stopIds;
                    if (stopIds.length > 0) {
                        let startStop = this.stopCollector.get(stopIds[0]);
                        let finishStop = this.stopCollector.get(stopIds[stopIds.length - 1]);

                        if (startStop)
                            this.selectedRoute.directScheduleInfo.startStopName = startStop.name;
                        if (finishStop)
                            this.selectedRoute.directScheduleInfo.finishStopName = finishStop.name;
                    }
                }
            })
        });
    }
}

class ScheduleTime {
    time: Date;
    isActive: boolean;
}

class InternalRoute {
    private _route: Route;
    get route(): Route { return this._route }

    number: string;
    id: number;
    isSelected: boolean;

    directScheduleInfo: RouteScheduleInfo;
    reverseScheduleInfo: RouteScheduleInfo;

    constructor(route: Route) {
        this._route = route;
    }
}

class RouteScheduleInfo {
    startTime: Date;
    endTime: Date;
    interval: number;

    startStopName: string;
    finishStopName: string;
}


// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/pages/schedule.component.ts